import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Button } from 'reactstrap';
import moment from 'moment';
import * as userService from '../Services/userService';
import { InfoTag } from '../Components/shared/infoTag';
import { basicDateFormat } from '../Helpers/dateTimeHelper';
import { useForm } from 'react-hook-form';
import FormProfileImageHandler from '../Components/ReactHookFormCustom/FormProfileImageHandler';
import FormInput from '../Components/ReactHookFormCustom/FormInput';
import { emailFormat } from '../Components/ReactHookFormCustom/FormHelpers';
import { SpinnerLoader } from '../Components/shared/spinnerLoader';
import useAuthStore from '../../../stores/authStore';
import { SpinnerLoaderType } from '../Helpers/enums';

const UserProfile = ({ userFromDbToEdit }) => {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);

  const { handleSubmit, setValue, reset, control, formState } = useForm({});
  const [isLoading, setIsLoading] = useState(false);
  const { isDirty } = formState;

  const handleValidProfileSubmit = (values) => {
    if (user.role === 'Admin' && userFromDbToEdit) {
      userService.updateUserAsAdmin(values, userFromDbToEdit);
    } else {
      setIsLoading(true);
      userService
        .updateUser(values)
        .then((response) => {
          useAuthStore.getState().setUserDataSettings(response.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // Admin can select other users and edit them
  const editedUser = userFromDbToEdit ? userFromDbToEdit : user;

  useEffect(() => {
    reset({
      firstName: editedUser?.firstName,
      lastName: editedUser?.lastName,
      email: editedUser?.email,
      userName: editedUser?.username,
      created: moment.utc(editedUser.created).format(basicDateFormat),
      userProfilePhoto: editedUser?.photoUrl,
    });
  }, [editedUser]);

  return (
    <Card className="card-default col-xl-8">
      <div className="card-header bg-gray-lighter text-bold">{t('settings.PROFILE')}</div>
      <div className="card-body">
        <form onSubmit={handleSubmit(handleValidProfileSubmit)}>
          <Row className="align-items-center justify-content-center pb-3">
            <Col xs="12" md="6" lg="3" style={{ maxWidth: '120px', minWidth: '30px' }}>
              <FormProfileImageHandler
                control={control}
                name="userProfilePhoto"
                imgUrl={editedUser?.photoUrl}
                id="userProfilePhotoId"
                setValue={setValue}
              />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col>
              <FormInput
                control={control}
                name="firstName"
                label={t('common.FIRST_NAME')}
                type="text"
                rules={{
                  required: t('common.REQUIRED'),
                }}
              />
            </Col>
            <Col>
              <FormInput
                control={control}
                name="lastName"
                label={t('common.LAST_NAME')}
                type="text"
                rules={{
                  required: t('common.REQUIRED'),
                }}
              />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col>
              <FormInput
                control={control}
                name="email"
                id="recruitersEmail"
                label={
                  <>
                    {t('common.E_MAIL')}
                    &nbsp;
                    <InfoTag id="recruitersEmail" lng={i18n.language} />
                  </>
                }
                type="email"
                rules={{
                  required: t('common.REQUIRED'),
                  pattern: {
                    value: emailFormat,
                    message: t('settings.EMAIL_ERROR'),
                  },
                }}
              />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col>
              <FormInput control={control} name="userName" label={t('common.USERNAME')} type="text" disabled />
            </Col>
            <Col>
              <FormInput control={control} name="created" disabled label={t('common.CREATED')} type="text" />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col>
              <b>
                {t('settings.USER_ROLES')}{' '}
                <ul>
                  {editedUser.roles?.map((role, index) => (
                    <li key={index}>{role}</li>
                  ))}
                </ul>
              </b>
            </Col>
          </Row>
          <Row>
            <Col>
              {isLoading ? (
                <SpinnerLoader loaderType={SpinnerLoaderType.Small} />
              ) : (
                <>
                  <Button color="info" disabled={!isDirty}>
                    {t('common.SAVE')}
                  </Button>
                  {user.role === 'Admin' && userFromDbToEdit ? (
                    <>
                      &nbsp;&nbsp;
                      <Button color="warning">{t('settings.SAVE_AS_ADMIN')}</Button>
                    </>
                  ) : null}
                </>
              )}
            </Col>
          </Row>
        </form>
      </div>
    </Card>
  );
};

export default UserProfile;
